/**
 * Canvas Serialization Utilities
 *
 * Provides robust serialization of Fabric.js canvases with custom asset properties.
 * Fabric.js doesn't automatically serialize custom properties set via direct assignment,
 * so we need to manually copy them from the live objects to the JSON output.
 */

import type { Canvas, FabricObject } from 'fabric';
import { logger } from '@/utils/logger';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

/**
 * Asset properties that need to be manually copied during serialization
 */
export const ASSET_CUSTOM_PROPERTIES = [
  'assetId',
  'assetType',
  'originalUrl',
  /**
   * Natural size of the uploaded original when the canvas holds a reduced
   * bitmap instead (phone uploads). The saved object has to describe the
   * original, which is what every later load draws.
   */
  'originalWidth',
  'originalHeight',
  '_uploadStatus',
] as const;

export type AssetCustomProperty = (typeof ASSET_CUSTOM_PROPERTIES)[number];

/**
 * Extended Fabric object with asset properties
 */
export interface FabricObjectWithAsset extends FabricObject {
  assetId?: string;
  assetType?: 'local' | 's3';
  originalUrl?: string;
  originalWidth?: number;
  originalHeight?: number;
  _uploadStatus?: 'pending' | 'completed' | 'failed';
}

/**
 * Canvas JSON object structure
 */
interface CanvasJSON {
  version?: string;
  objects?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

// ═══════════════════════════════════════════════════════════════════
// MAIN SERIALIZATION FUNCTION
// ═══════════════════════════════════════════════════════════════════

/**
 * Serialize canvas to JSON with robust custom property handling
 *
 * This function:
 * 1. Calls canvas.toJSON() - custom properties are now globally configured
 * 2. Iterates through live canvas objects
 * 3. Copies custom asset properties to the JSON output
 * 4. Replaces blob URLs with permanent URLs from live objects
 *
 * @param canvas - Fabric.js canvas instance
 * @param _customProperties - DEPRECATED: No longer used (Fabric.js 6.x uses FabricObject.customProperties)
 * @returns Serialized canvas JSON with all custom properties
 */
export function serializeCanvasWithAssets(
  canvas: Canvas,
  _customProperties: string[] = []
): CanvasJSON {
  // 1. Get standard serialization
  // Custom properties are now globally configured via FabricObject.customProperties (config/fabricConfig.ts)
  const json = canvas.toJSON() as CanvasJSON;

  if (!json.objects || !Array.isArray(json.objects)) {
    return json;
  }

  // 2. Get live objects from canvas
  const liveObjects = canvas.getObjects() as FabricObjectWithAsset[];

  // 3. Copy custom properties from live objects to JSON
  // Objects are in the same order in both arrays
  for (let i = 0; i < liveObjects.length && i < json.objects.length; i++) {
    const liveObj = liveObjects[i];
    const jsonObj = json.objects[i];
    if (!liveObj || !jsonObj) continue;

    // Copy asset properties
    for (const prop of ASSET_CUSTOM_PROPERTIES) {
      const value = liveObj[prop as keyof FabricObjectWithAsset];
      if (value !== undefined) {
        jsonObj[prop] = value;
      }
    }

    // Copy the current src from the live object (may be updated after setSrc)
    // This ensures we save the permanent URL, not the blob URL
    if (liveObj.type === 'image' || liveObj.type === 'Image') {
      const imgObj = liveObj as unknown as {
        src?: string;
        _element?: HTMLImageElement;
      };

      // Priority: Use element.src (most up-to-date), then fall back to stored src
      if (imgObj._element?.src && !imgObj._element.src.startsWith('blob:')) {
        jsonObj.src = imgObj._element.src;
      } else if (imgObj.src && !imgObj.src.startsWith('blob:')) {
        jsonObj.src = imgObj.src;
      }
      // If still a blob URL, leave as-is (will be filtered out later if temp asset)
    }
  }

  logger.debug(
    'CanvasSerializer',
    `Serialized canvas with ${json.objects.length} objects`
  );

  return json;
}

// ═══════════════════════════════════════════════════════════════════
// FABRIC OBJECT HELPERS
// ═══════════════════════════════════════════════════════════════════

/**
 * Set asset properties on a Fabric object
 *
 * Uses both direct assignment and Fabric's set() method to ensure
 * properties are available for both JS access and internal Fabric state.
 *
 * @param obj - Fabric object to modify
 * @param props - Asset properties to set
 */
export function setAssetProperties(
  obj: FabricObject,
  props: {
    assetId?: string;
    assetType?: 'local' | 's3';
    originalUrl?: string;
    originalWidth?: number;
    originalHeight?: number;
    _uploadStatus?: 'pending' | 'completed' | 'failed';
  }
): void {
  const target = obj as FabricObjectWithAsset;

  // Direct property assignment for serialization
  if (props.assetId !== undefined) target.assetId = props.assetId;
  if (props.assetType !== undefined) target.assetType = props.assetType;
  if (props.originalUrl !== undefined) target.originalUrl = props.originalUrl;
  if (props.originalWidth !== undefined) {
    target.originalWidth = props.originalWidth;
  }
  if (props.originalHeight !== undefined) {
    target.originalHeight = props.originalHeight;
  }
  if (props._uploadStatus !== undefined)
    target._uploadStatus = props._uploadStatus;

  // Mark object as dirty to trigger re-render
  obj.setCoords();
}

/**
 * Get asset properties from a Fabric object
 */
export function getAssetProperties(obj: FabricObject): {
  assetId?: string;
  assetType?: 'local' | 's3';
  originalUrl?: string;
  _uploadStatus?: 'pending' | 'completed' | 'failed';
} {
  const target = obj as FabricObjectWithAsset;
  return {
    assetId: target.assetId,
    assetType: target.assetType,
    originalUrl: target.originalUrl,
    _uploadStatus: target._uploadStatus,
  };
}

/**
 * Check if a Fabric object has a completed asset upload
 */
export function hasCompletedAsset(obj: FabricObject): boolean {
  const target = obj as FabricObjectWithAsset;
  return (
    target.assetId !== undefined &&
    !target.assetId.startsWith('temp_') &&
    target._uploadStatus === 'completed'
  );
}

/**
 * Check if a Fabric object has a pending upload
 */
export function hasPendingUpload(obj: FabricObject): boolean {
  const target = obj as FabricObjectWithAsset;
  return (
    target.assetId !== undefined &&
    (target.assetId.startsWith('temp_') || target._uploadStatus === 'pending')
  );
}

// ═══════════════════════════════════════════════════════════════════
// COORDINATE TRANSFORMATION (Full-Mockup Canvas)
// ═══════════════════════════════════════════════════════════════════

/**
 * Print area offset for coordinate transformation.
 * Used when canvas is mockup-sized and print area is positioned within.
 */
export interface PrintAreaOffset {
  x: number;
  y: number;
}

/**
 * Transform coordinates from Canvas space to Print-Area space.
 * Used when SAVING canvas data.
 *
 * Canvas coordinates have print area offset.
 * Print-Area coordinates are relative to (0, 0).
 *
 * @param canvasJson - Canvas JSON with canvas-space coordinates
 * @param offset - Print area offset (x, y)
 * @param filterSystemLayers - Whether to filter out system layers (default: true)
 * @returns JSON with print-area-space coordinates
 */
export function transformToSaveCoordinates(
  canvasJson: CanvasJSON,
  _offset: PrintAreaOffset,
  filterSystemLayers = true
): CanvasJSON {
  if (!canvasJson.objects || !Array.isArray(canvasJson.objects)) {
    return canvasJson;
  }

  const transformedObjects = canvasJson.objects
    // Filter system layers if requested
    .filter((obj) => {
      if (!filterSystemLayers) return true;
      const name = obj.name as string | undefined;
      return !name || !name.startsWith('__');
    })
    // Transform coordinates
    .map((obj) => ({
      ...obj,
      left: (obj.left as number) || 0,
      top: (obj.top as number) || 0,
    }));

  return {
    ...canvasJson,
    objects: transformedObjects,
  };
}

/**
 * Transform coordinates from Print-Area space to Canvas space.
 * Used when LOADING canvas data.
 *
 * Print-Area coordinates are relative to (0, 0).
 * Canvas coordinates have print area offset.
 *
 * @param savedJson - Saved JSON with print-area-space coordinates
 * @param offset - Print area offset (x, y)
 * @returns JSON with canvas-space coordinates
 */
export function transformToLoadCoordinates(
  savedJson: CanvasJSON,
  _offset: PrintAreaOffset
): CanvasJSON {
  if (!savedJson.objects || !Array.isArray(savedJson.objects)) {
    return savedJson;
  }

  const transformedObjects = savedJson.objects.map((obj) => ({
    ...obj,
    left: (obj.left as number) || 0,
    top: (obj.top as number) || 0,
  }));

  return {
    ...savedJson,
    objects: transformedObjects,
  };
}
